import type { DrefAcceptedTerm, DrefTerm } from '../types/drf/drfType';

export const DRF_REQUEST_TIMEOUT_MS = 30_000;
export const DRF_REQUEST_TIMEOUT_MESSAGE = 'Tempo limite excedido. Tente novamente.';
export const DRF_MAX_ATTEMPTS = 1;
export const DRF_LOAD_DATA_ERROR_MESSAGE = 'Não foi possível carregar os dados. Tente novamente.';
export const DRF_LIST_TERMS_VALIDATION_ERROR_MESSAGE =
  'Não foi possível validar o Aceite dos Termos de Uso.';

export type DrfTermsFailureContext = 'gate' | 'save' | null;

export const DRF_LIST_PAGINATION = {
  limit: 5,
  offset: 1,
  orderBy: 1,
} as const;

export const DRF_PERSON_TYPE = 'F';

export type DrfPortalTermTabId = 'termo-uso' | 'termo-adesao' | 'aviso-privacidade';

export interface DrfPortalTermDefinition {
  id: DrfPortalTermTabId;
  title: string;
  associationId: string;
}

export interface DrfTermContent {
  title: string;
  description: string;
  updatedAt: string;
}

export type DrfTermContentState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; content: DrfTermContent };

export interface UseDrfTermsReturn {
  termsCheckLoading: boolean;
  showDrfTermModal: boolean;
  showDrfListTermsFailure: boolean;
  saveLoading: boolean;
  saveError: string | null;
  failureContext: DrfTermsFailureContext;
  termContentByTab: Record<string, DrfTermContentState>;
  allTabsViewed: boolean;
  portalTermDefinitions: readonly DrfPortalTermDefinition[];
  skipDrf: boolean;
  runTermsGate: () => Promise<boolean>;
  selectTermTab: (tabId: string) => void;
  savePendingTerms: () => Promise<boolean>;
  retryLastAction: () => Promise<boolean>;
  abandonDrfTerms: () => void;
}

export function withRequestTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number = DRF_REQUEST_TIMEOUT_MS,
  message: string = DRF_REQUEST_TIMEOUT_MESSAGE,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  });
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  maxAttempts: number = DRF_MAX_ATTEMPTS,
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError instanceof Error && lastError.message === DRF_REQUEST_TIMEOUT_MESSAGE) {
    throw new Error(DRF_LOAD_DATA_ERROR_MESSAGE);
  }

  throw new Error(DRF_LOAD_DATA_ERROR_MESSAGE);
}

export function resolveDrfPersonId(cpf?: string | null): string {
  return String(cpf ?? '').replace(/\D/g, '');
}

export function getLatestTermVersion(terms: DrefTerm[]): number {
  return terms.reduce((latest, term) => {
    const version = Number(term.versao);
    return Number.isFinite(version) && version > latest ? version : latest;
  }, 0);
}

export function parseDrefVigenciaDate(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  return new Date(year, month - 1, day);
}

/** `verifyAcceptance`: acceptance exists when `termo` is null or has `dataTermoAceite`. */
export function isVerifyAcceptanceSatisfied(term: DrefAcceptedTerm | null | undefined): boolean {
  if (term == null) {
    return true;
  }

  return String(term.dataTermoAceite ?? '').trim().length > 0;
}

const DRF_HEADING_TAG_LEVELS = new Set(['1', '2', '3', '4', '5']);

function isDrfHeadingTagName(tagName: string): boolean {
  const normalized = tagName.toLowerCase();
  if (!normalized.startsWith('h') || normalized.length !== 2) {
    return false;
  }
  return DRF_HEADING_TAG_LEVELS.has(normalized[1] ?? '');
}

function splitTagNameAndAttributes(tagBody: string): { name: string; attributes: string } {
  let index = 0;
  while (index < tagBody.length) {
    const char = tagBody[index];
    if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
      break;
    }
    index += 1;
  }

  return {
    name: tagBody.slice(0, index).toLowerCase(),
    attributes: tagBody.slice(index),
  };
}

/** Downgrades h1–h5 from DREF HTML descriptions so headings fit the modal layout. */
export function normalizeDrfTermHeadingTags(html: string): string {
  if (!html) {
    return html;
  }

  let result = '';
  let index = 0;

  while (index < html.length) {
    const tagStart = html.indexOf('<', index);
    if (tagStart === -1) {
      result += html.slice(index);
      break;
    }

    result += html.slice(index, tagStart);
    const tagEnd = html.indexOf('>', tagStart);
    if (tagEnd === -1) {
      result += html.slice(tagStart);
      break;
    }

    const rawTag = html.slice(tagStart + 1, tagEnd).trim();
    const isClosing = rawTag.startsWith('/');
    const tagBody = isClosing ? rawTag.slice(1).trim() : rawTag;
    const { name, attributes } = splitTagNameAndAttributes(tagBody);

    if (isDrfHeadingTagName(name)) {
      result += isClosing ? '</h6>' : `<h6${attributes}>`;
    } else {
      result += html.slice(tagStart, tagEnd + 1);
    }

    index = tagEnd + 1;
  }

  return result;
}

export function formatDrefVigenciaDisplay(value: string): string {
  const date = parseDrefVigenciaDate(value);
  if (!date) {
    return value;
  }

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}
